import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { Info } from "lucide-react";
import { Controller, FormProvider, useFormContext } from "react-hook-form";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import type * as LabelPrimitive from "@radix-ui/react-label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn("space-y-2 mb-6", className)} {...props} />
      </FormItemContext.Provider>
    );
  }
);

FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return <Label ref={ref} className={cn(error && "text-lava-500", className)} htmlFor={formItemId} {...props} />;
});

FormLabel.displayName = "FormLabel";

type FormLabelWithCountProps = {
  label: React.ReactNode;
  current: number;
  max: number;
  className?: string;
};

const FormLabelWithCount = ({ label, current, max, className }: FormLabelWithCountProps) => {
  const isOverLimit = current > max;

  return (
    <div className={cn("flex justify-between", className)}>
      <Label>{label}</Label>
      <span className={cn("text-xs", isOverLimit ? "text-lava-500" : "text-ash-500")}>
        {current}/{max} chars
      </span>
    </div>
  );
};

FormLabelWithCount.displayName = "FormLabelWithCount";

const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
  ({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      />
    );
  }
);

FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return <p ref={ref} id={formDescriptionId} className={cn("text-sm text-ash-500", className)} {...props} />;
  }
);

FormDescription.displayName = "FormDescription";

type FormMessageProps = React.HTMLAttributes<HTMLParagraphElement> & {
  icon?: React.ReactNode;
};

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, icon, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) {
      return null;
    }

    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn("flex items-start gap-2 text-sm font-medium text-lava-500", className)}
        {...props}
      >
        {icon && <span className="mt-0.5">{icon}</span>}
        <span>{body}</span>
      </p>
    );
  }
);

FormMessage.displayName = "FormMessage";

type HintMessageProps = React.HTMLAttributes<HTMLDivElement> & {
  icon?: React.ReactNode;
};

const HintMessage = React.forwardRef<HTMLDivElement, HintMessageProps>(
  ({ className, children, icon = <Info className="h-4 w-4 text-ash-500" />, ...props }, ref) => {
    if (!children) {
      return null;
    }

    return (
      <div ref={ref} className={cn("flex items-start gap-2 text-sm font-medium text-ash-500", className)} {...props}>
        <span className="mt-0.5">{icon}</span>
        <span>{children}</span>
      </div>
    );
  }
);

HintMessage.displayName = "HintMessage";

/**
 * layout-stable error slot - always reserves a single line of vertical space
 * so content below doesn't shift when an error appears or disappears.
 * unlike FormMessage, this is context-free and works outside FormField.
 */
function FormErrorSlot({ message, className }: { message?: string; className?: string }) {
  return (
    <div className={cn("min-h-5 -mb-4", className)}>
      {message && <p className="text-sm text-lava-500">{message}</p>}
    </div>
  );
}

export {
  Form,
  FormControl,
  FormDescription,
  FormErrorSlot,
  FormField,
  FormItem,
  FormLabel,
  FormLabelWithCount,
  FormMessage,
  HintMessage,
  useFormField,
};
